import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useAARData } from "../../context/AARDataContext";
import { useAuth } from "../../context/AuthContext";
import { getAARFile } from "../../appwrite/appwrite";

import ActionTracker from "../ActionTracker/ActionTracker";

import "./AARDetails.css";

const formatFileSize = (bytes) => {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const unitIndex = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1
  );
  return `${(bytes / 1024 ** unitIndex).toFixed(unitIndex ? 1 : 0)} ${units[unitIndex]}`;
};

function AARDetails() {
  const { id } = useParams();
  const [isAttachmentAvailable, setIsAttachmentAvailable] = useState(true);

  const { projects, approveAAR } = useAARData();
  const { role } = useAuth();
  const [isApproving, setIsApproving] = useState(false);
  const [approvalError, setApprovalError] = useState("");

  const project = projects.find(
    (item) => item.id === Number(id)
  );

  useEffect(() => {
    let isMounted = true;

    const attachmentId = project?.attachment?.fileId;

    if (!attachmentId) {
      setIsAttachmentAvailable(true);
      return () => {
        isMounted = false;
      };
    }

    setIsAttachmentAvailable(null);

    getAARFile(attachmentId)
      .then(() => {
        if (isMounted) {
          setIsAttachmentAvailable(true);
        }
      })
      .catch(() => {
        if (isMounted) {
          setIsAttachmentAvailable(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [project?.attachment?.fileId]);

  async function handleApprove() {
    if (!project) return;

    setIsApproving(true);
    setApprovalError("");

    try {
      await approveAAR(project);
    } catch {
      setApprovalError("The AAR could not be approved.");
    } finally {
      setIsApproving(false);
    }
  }

  /* ==========================================
     PROJECT NOT FOUND
  ========================================== */

  if (!project) {
    return (
      <main className="aar-not-found">

        <h1>AAR Not Found</h1>

        <p>
          The requested project could not be found.
        </p>

        <Link to="/">
          ← Back to AAR Library
        </Link>

      </main>
    );
  }


  return (
    <main className="aar-details">

      {/* =====================================
          PROJECT HEADER
      ====================================== */}

      <section className="aar-details-header">

        <div className="aar-header-container">

          <Link
            to="/"
            className="back-link"
          >
            ← Back to AAR Library
          </Link>


          <div className="aar-header-content">

            <div>

              <span className="aar-type">
                {project.type}
              </span>

              <h1>
                {project.title}
              </h1>

              <p>
                {project.subtitle}
              </p>

            </div>


            <span className="aar-status">
              {project.status}
            </span>

            {(role === "admin" || role === "reviewer") &&
              String(project.status || "").toUpperCase() === "UNDER REVIEW" && (
                <button
                  type="button"
                  className="aar-approve-button"
                  onClick={handleApprove}
                  disabled={isApproving}
                >
                  {isApproving ? "Approving..." : "Approve AAR"}
                </button>
              )}

          </div>

          {approvalError && <p className="aar-approval-error">{approvalError}</p>}


          <div className="aar-header-meta">

            <span>
              📍 {project.location}
            </span>

            <span>
              📅 {project.date}
            </span>

          </div>

        </div>

      </section>


      {/* =====================================
          PROJECT IMAGE
      ====================================== */}

      <section className="aar-image-section">

        <img
          src={project.image}
          alt={project.title}
        />

      </section>


      {/* =====================================
          PROJECT OVERVIEW
      ====================================== */}

      <section className="aar-section">

        <div className="aar-section-heading">

          <span>
            01
          </span>

          <div>

            <h2>
              Project Overview
            </h2>

            <p>
              Understanding the project and its outcome.
            </p>

          </div>

        </div>


        <div className="aar-overview-card">

          <p>
            {project.overview || "No project overview available."}
          </p>

        </div>

      </section>

      {project.attachment && (
        <section className="aar-section">
          <div className="aar-section-heading">
            <span>06</span>
            <div>
              <h2>Document</h2>
              <p>Attached source document for this AAR.</p>
            </div>
          </div>

          <div className={`aar-attachment-card ${isAttachmentAvailable === false ? "unavailable" : ""}`}>
            <div>
              <strong>{project.attachment.fileName}</strong>
              <span>
                {project.attachment.fileType || "Document"} • {formatFileSize(project.attachment.fileSize)}
              </span>
            </div>

            {isAttachmentAvailable === null && (
              <span className="aar-attachment-status">Checking document...</span>
            )}

            {isAttachmentAvailable === false && (
              <span className="aar-attachment-status">
                This document is no longer available.
              </span>
            )}

            {isAttachmentAvailable === true && (
              <div className="aar-attachment-actions">
                <a href={project.attachment.fileUrl} target="_blank" rel="noreferrer">
                  View Document
                </a>
                <a href={project.attachment.fileUrl} download={project.attachment.fileName}>
                  Download
                </a>
              </div>
            )}
          </div>
        </section>
      )}


      {/* =====================================
          PROJECT PERFORMANCE
      ====================================== */}

      <section className="aar-section">

        <div className="aar-section-heading">

          <span>
            02
          </span>

          <div>

            <h2>
              Project Performance
            </h2>

            <p>
              Review of key project performance areas.
            </p>

          </div>

        </div>


        <div className="performance-grid">

          <div className="performance-card">

            <span>
              SAFETY
            </span>

            <strong>
              {project.performance?.safety || "N/A"}
            </strong>

          </div>


          <div className="performance-card">

            <span>
              QUALITY
            </span>

            <strong>
              {project.performance?.quality || "N/A"}
            </strong>

          </div>


          <div className="performance-card">

            <span>
              COST
            </span>

            <strong>
              {project.performance?.cost || "N/A"}
            </strong>

          </div>


          <div className="performance-card">

            <span>
              SCHEDULE
            </span>

            <strong>
              {project.performance?.schedule || "N/A"}
            </strong>

          </div>

        </div>

      </section>


      {/* =====================================
          WHAT WENT WELL / IMPROVEMENTS
      ====================================== */}

      <section className="aar-section">

        <div className="learning-grid">

          {/* WHAT WENT WELL */}

          <div className="learning-card">

            <span className="learning-number">
              03
            </span>

            <h2>
              What Went Well
            </h2>

            {Array.isArray(project.whatWentWell) &&
            project.whatWentWell.length > 0 ? (

              <ul>

                {project.whatWentWell.map(
                  (item, index) => (

                    <li key={index}>
                      {item}
                    </li>

                  )
                )}

              </ul>

            ) : (

              <p>
                No information recorded.
              </p>

            )}

          </div>


          {/* IMPROVEMENTS */}

          <div className="learning-card">

            <span className="learning-number">
              04
            </span>

            <h2>
              What Could Be Improved
            </h2>

            {Array.isArray(project.improvements) &&
            project.improvements.length > 0 ? (

              <ul>

                {project.improvements.map(
                  (item, index) => (

                    <li key={index}>
                      {item}
                    </li>

                  )
                )}

              </ul>

            ) : (

              <p>
                No improvements recorded.
              </p>

            )}

          </div>

        </div>

      </section>


      {/* =====================================
          LESSONS LEARNED
      ====================================== */}

      <section className="aar-section">

        <div className="aar-section-heading">

          <span>
            05
          </span>

          <div>

            <h2>
              Lessons Learned
            </h2>

            <p>
              Key knowledge captured from the project.
            </p>

          </div>

        </div>


        <div className="lessons-list">

          {Array.isArray(project.lessonsLearned) &&
          project.lessonsLearned.length > 0 ? (

            project.lessonsLearned.map(
              (lesson, index) => (

                <div
                  className="lesson-item"
                  key={index}
                >

                  <span>
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <p>
                    {lesson}
                  </p>

                </div>

              )
            )

          ) : (

            <div className="lesson-item">

              <span>
                01
              </span>

              <p>
                No lessons learned have been recorded.
              </p>

            </div>

          )}

        </div>

      </section>


      {/* =====================================
          ACTION TRACKER
      ====================================== */}

      <section className="aar-section">

        <div className="aar-section-heading">

          <span>
            06
          </span>

          <div>

            <h2>
              Action Tracker
            </h2>

            <p>
              Improvement actions resulting from this AAR.
            </p>

          </div>

        </div>


        <ActionTracker
          projectId={project.id}
          projectTitle={project.title}
          actions={project.actions || []}
        />

      </section>

    </main>
  );
}

export default AARDetails;